import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('App error caught by boundary:', error, info.componentStack);
    // After a deploy an open page may ask for a code chunk that no longer
    // exists — reload once to get the new version instead of an error screen.
    const msg = `${(error && error.name) || ''} ${(error && error.message) || ''}`;
    if (
      /ChunkLoadError|Loading (CSS )?chunk|dynamically imported module/i.test(
        msg
      )
    ) {
      try {
        if (!sessionStorage.getItem('cp_chunk_reload')) {
          sessionStorage.setItem('cp_chunk_reload', '1');
          window.location.reload();
        }
      } catch (e) {
        /* storage blocked — keep the error screen */
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            p: 3,
            bgcolor: '#f9fafb',
          }}
        >
          <Paper
            sx={{
              p: 5,
              maxWidth: 520,
              textAlign: 'center',
              borderRadius: 4,
              boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            }}
          >
            <BugReportIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mb: 3 }}
            >
              Refresh the page or click Try Again to recover without losing your
              session.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
              <Button
                variant="contained"
                onClick={() => this.setState({ hasError: false, error: null })}
              >
                Try Again
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}
